// Projects table (Fig33) — every project the user owns, one row each, with a
// pipeline stage pill, filter tabs, compare mode, and a per-row actions menu.
// Replaces the old project-card grid that used to live on this route.
import { uploadLinkFor } from '../../utils/uploadLink'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, FolderOpen, MoreVertical, GitCompare, Upload, Eye, EyeOff, Pencil, Trash2, FolderOpen as OpenWorkspaceIcon } from 'lucide-react'
import { createProject, deleteProject, updateProject, getDisclosureAgreements } from '../../services/api'
import type { Project } from '../../types'
import PageHeader from '../ui/PageHeader'
import EmptyState from '../ui/EmptyState'
import ConfirmDialog from '../ui/ConfirmDialog'
import ContextualTip from '../ui/ContextualTip'
import StatusChip from '../ui/StatusChip'
import CompareProjectsPanel from './CompareProjectsPanel'
import GrantAggregatorAccessDialog from './GrantAggregatorAccessDialog'
import { PROJECT_TABS, type ProjectTabKey, projectMatchesTab, getProjectStagePill } from '../../utils/projectStage'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import { projectInitials, formatRelativeTime } from '../../utils/projectDisplay'
import { recoveryEntry } from '../recovery/recoveryLink'

/**
 * `showHeader` (Story #5648 build v2, figures S4/S4b): the table is now one tab
 * of "My inventions", and that screen renders the one page header the figure
 * shows. Passing false drops THIS component's own title/description/primary
 * action so the page does not carry two headers — the secondary controls (New
 * Project, Compare) are kept and rendered on their own row, because they are
 * real actions the figure's header has no room for.
 */
export default function ProjectsTable({
  projects,
  showHeader = true,
}: {
  projects: Project[]
  showHeader?: boolean
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // Story #6490 (Bug #4838) Stream 0c.1 — organisation attribution comes from
  // the SELECTED WORKSPACE, sent in the POST body and validated server-side
  // (never from the sticky Cognito claim). Personal workspace -> null.
  const { active: activeWorkspace } = useWorkspace()
  const workspaceOrgId =
    activeWorkspace.kind === 'organisation' && activeWorkspace.entityId ? activeWorkspace.entityId : null

  const { data: agreements } = useQuery({ queryKey: ['disclosure-agreements'], queryFn: () => getDisclosureAgreements() })

  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState<ProjectTabKey>('all')
  const [compareMode, setCompareMode] = useState(false)
  const [selectedForCompare, setSelectedForCompare] = useState<string[]>([])
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)

  // Create-project modal state
  const [showCreate, setShowCreate] = useState(false)
  const [newName, setNewName] = useState('')
  const [newVertical, setNewVertical] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [newDeclaration, setNewDeclaration] = useState('')
  const [newDiscoverable, setNewDiscoverable] = useState(false)
  const [createError, setCreateError] = useState('')
  // T10: after creating a project, offer to grant a managing aggregator access.
  const [grantPromptProjectId, setGrantPromptProjectId] = useState<string | null>(null)

  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null)
  const [lastArchivedId, setLastArchivedId] = useState<string | null>(null)
  const [renameTarget, setRenameTarget] = useState<Project | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [renameError, setRenameError] = useState('')

  const createMutation = useMutation({
    mutationFn: async () => {
      const project = await createProject({
        name: newName,
        vertical: newVertical.trim(),
        description: newDesc,
        invention_declaration: newDeclaration.trim() || undefined,
        status: 'active',
        discoverable: newDiscoverable,
        org_id: workspaceOrgId,
      })
      // Delete-and-explain guard (Story #6490 round 6): a backend that does not
      // know org_id returns 201 with the field silently dropped, which would
      // leave an org-workspace project stranded in the personal workspace —
      // outside the organisation's deletion set when its key is revoked. If
      // the server did not record what we sent, undo it and say so.
      if ((project.org_id ?? null) !== workspaceOrgId) {
        const where = activeWorkspace.name
        try {
          await deleteProject(project.project_id)
        } catch {
          throw new Error(
            `A project was created in your personal workspace instead of ${where}; please delete it`,
          )
        }
        throw new Error(
          `The project could not be created in ${where} because the server did not record the organisation. Nothing was created — try again later.`,
        )
      }
      return project
    },
    onSuccess: (project) => {
      queryClient.setQueryData<Project[]>(['projects'], (old) => [...(old || []), { ...project, document_count: 0, collaborator_count: 0 }])
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['projects'] })
      }, 2000)
      setShowCreate(false)
      setNewName('')
      setNewVertical('')
      setNewDesc('')
      setNewDeclaration('')
      setNewDiscoverable(false)
      setCreateError('')
      setGrantPromptProjectId(project.project_id)
    },
    onError: (err: Error & { response?: { data?: { error?: string } } }) => {
      const code = err.response?.data?.error
      if (code === 'org_not_member') {
        // create_project's 403: the caller holds no LIVE membership of the
        // selected organisation (e.g. suspended via SCIM). Nothing was created.
        setCreateError(
          `You are not an active member of ${activeWorkspace.name}, so the project was not created.`,
        )
        return
      }
      setCreateError(code || err.message || 'Failed to create project')
    },
  })

  const toggleDiscoverableMutation = useMutation({
    mutationFn: ({ id, discoverable }: { id: string; discoverable: boolean }) => updateProject(id, { discoverable }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteProject(id),
    onSuccess: (_result, projectId) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      setLastArchivedId(projectId)
      setDeleteTarget(null)
    },
    // BUG-126: a failed delete must give feedback — ConfirmDialog owns that via
    // onConfirm below awaiting mutateAsync and rethrowing the friendly message.
  })

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => updateProject(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      setRenameTarget(null)
      setRenameError('')
    },
    onError: (err: Error & { response?: { data?: { error?: string } } }) => {
      setRenameError(err.response?.data?.error || err.message || 'Failed to rename project')
    },
  })

  const hasProjects = projects.length > 0
  const recovery = recoveryEntry(typeof window === 'undefined' ? null : window.INNOVATEFLOW_CONFIG)
  function renderRecoveryEntry() {
    let destination: string | undefined
    if (recovery.available) {
      const url = new URL(recovery.applicationUrl)
      url.searchParams.set('recovery_view', 'recover')
      url.searchParams.set('recovery_kind', 'project_reactivate')
      if (lastArchivedId) url.searchParams.set('recovery_project', lastArchivedId)
      destination = url.toString()
    }
    return (
      <div className="my-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
        {lastArchivedId && <p role="status">Project archived. Recovery reference: <code>{lastArchivedId}</code>.</p>}
        {destination ? (
          <a href={destination} target="_blank" rel="noopener noreferrer" className="font-medium text-iflow-700 underline">
            Review archived project recovery in the Management Portal
          </a>
        ) : <p>Archived project recovery requires an authorised administrator. {recovery.available ? '' : recovery.reason}</p>}
        <p className="mt-1 text-xs">Recovery requires a separate administrator sign-in, an exact project preview and approval. Current permissions remain in force.</p>
        <p className="mt-3">
          <Link to="/recovery/project" className="font-medium text-iflow-700 underline">Confirm project recovery</Link>
          <span className="ml-2">Use the project ID and recovery operation ID supplied by your administrator.</span>
        </p>
      </div>
    )
  }

  if (!hasProjects) {
    return (
      <div>
        {showHeader && (
          <PageHeader
            title="Projects"
            description="Every innovation you own, in one place — open a project to move it through the pipeline."
            helpKey="projects"
            primaryAction={
              <button
                data-tour="new-disclosure"
                onClick={() => navigate('/new')}
                className="flex items-center gap-2 rounded-lg bg-iflow-700 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-iflow-800"
              >
                <Plus size={16} /> New Disclosure
              </button>
            }
          />
        )}
        <EmptyState
          icon={FolderOpen}
          title="No projects yet"
          description="Create your first project to start organising and protecting your innovations. Each project groups the documents, analysis, matches and contacts for one invention."
          action={
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-iflow-700 px-4 py-2 text-sm font-medium text-white hover:bg-iflow-800"
            >
              <Plus size={16} /> Create Project
            </button>
          }
          secondary={
            <button onClick={() => navigate('/submit')} className="font-medium text-iflow-600 hover:text-iflow-700">
              Or start a guided New Disclosure →
            </button>
          }
        />
        {renderRecoveryEntry()}
        {renderCreateModal()}
      </div>
    )
  }

  const tabCounts: Record<ProjectTabKey, number> = {
    all: projects.length,
    active: projects.filter((p) => projectMatchesTab(p, 'active', agreements)).length,
    disclosed: projects.filter((p) => projectMatchesTab(p, 'disclosed', agreements)).length,
    draft: projects.filter((p) => projectMatchesTab(p, 'draft', agreements)).length,
  }

  const tabFiltered = projects.filter((p) => projectMatchesTab(p, activeTab, agreements))
  const filteredProjects = tabFiltered.filter(
    (p) => !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.description?.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  function toggleCompareSelection(project: Project) {
    const isSelected = selectedForCompare.includes(project.project_id)
    const atLimit = selectedForCompare.length >= 3 && !isSelected
    if (isSelected) {
      setSelectedForCompare((s) => s.filter((id) => id !== project.project_id))
    } else if (!atLimit) {
      setSelectedForCompare((s) => [...s, project.project_id])
    }
  }

  function renderCreateModal() {
    if (!showCreate) return null
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/40" onClick={() => setShowCreate(false)} />
        <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
          <h3 className="mb-4 text-lg font-semibold text-iflow-900">Create New Project</h3>
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Project Name</label>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="light-input w-full px-3 py-2 text-sm outline-none"
                placeholder="My Innovation Project"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Technical Vertical</label>
              <input
                value={newVertical}
                onChange={(e) => setNewVertical(e.target.value)}
                list="vertical-suggestions"
                className="light-input w-full px-3 py-2 text-sm outline-none"
                placeholder="e.g. Biotechnology, Clean Energy, AI/ML…"
              />
              <datalist id="vertical-suggestions">
                {['Biotechnology', 'Clean Energy', 'Materials Science', 'Computer Vision', 'AI/ML', 'Robotics', 'Telecommunications', 'Quantum Computing', 'Photonics', 'Medical Devices', 'AgriTech', 'FinTech'].map((v) => (
                  <option key={v} value={v} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Description</label>
              <textarea
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                rows={3}
                className="light-input w-full resize-none px-3 py-2 text-sm outline-none"
                placeholder="Describe your project..."
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Invention Declaration <span className="text-xs font-normal text-slate-400">(optional)</span>
              </label>
              <textarea
                value={newDeclaration}
                onChange={(e) => setNewDeclaration(e.target.value)}
                rows={3}
                className="light-input w-full resize-none px-3 py-2 text-sm outline-none"
                placeholder="What is your invention and what problem does it solve? A one-paragraph anchor for the AI analysis."
              />
              <p className="mt-1 text-xs text-slate-500">You can add or refine this later from the project's AI Analysis tab.</p>
            </div>
            <label className="flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                checked={newDiscoverable}
                onChange={(e) => setNewDiscoverable(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-iflow-500 accent-iflow-500 focus:ring-iflow-500"
              />
              <div>
                <span className="text-sm font-medium text-slate-700">Make discoverable</span>
                <p className="mt-0.5 text-xs text-slate-500">Allow other users to find similar inventions. Your content is never shared — only topics and similarity scores.</p>
              </div>
            </label>
          </div>
          {createError && <p className="mt-2 text-sm text-rose-600">{createError}</p>}
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setShowCreate(false)} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">
              Cancel
            </button>
            <button
              onClick={() => createMutation.mutate()}
              disabled={!newName.trim() || !newVertical.trim() || createMutation.isPending}
              className="rounded-lg bg-iflow-700 px-4 py-2 text-sm font-medium text-white hover:bg-iflow-800 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  const secondaryActions = (
    <>
      <button
        onClick={() => setShowCreate(true)}
        className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
      >
        <Plus size={15} /> New Project
      </button>
      {projects.length > 1 ? (
        <button
          onClick={() => {
            setCompareMode((m) => !m)
            setSelectedForCompare([])
          }}
          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
            compareMode ? 'border-iflow-300 bg-iflow-500/10 text-iflow-600 hover:bg-iflow-500/20' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
          data-testid="compare-mode-toggle"
        >
          <GitCompare size={15} />
          {compareMode ? 'Exit Compare' : 'Compare'}
        </button>
      ) : null}
    </>
  )

  return (
    <div>
      {showHeader ? (
        <PageHeader
          title="Projects"
          description="Every innovation you own, in one place — open a project to move it through the pipeline."
          helpKey="projects"
          status={{ label: `${projects.length} ${projects.length === 1 ? 'project' : 'projects'}`, tone: 'neutral' }}
          primaryAction={
            <button
              data-tour="new-disclosure"
              onClick={() => navigate('/new')}
              className="flex items-center gap-2 rounded-lg bg-iflow-700 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-iflow-800"
            >
              <Plus size={16} /> New Disclosure
            </button>
          }
          secondaryActions={secondaryActions}
        />
      ) : (
        <div className="mb-4 flex flex-wrap items-center justify-end gap-2">{secondaryActions}</div>
      )}

      <ContextualTip id="projects-pipeline-info">
        <strong>Pipeline stages:</strong> Upload → Intake → Analysis → Assessment → Review → Matching → Disclosure. A
        project advances one stage at a time; the coloured pill tells you which stage each project is at and what to
        do next.
      </ContextualTip>
      {renderRecoveryEntry()}

      <div className="flex flex-wrap items-center justify-between gap-3 mt-4 mb-4">
        <div className="flex items-center gap-1" role="tablist" aria-label="Filter projects">
          {PROJECT_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              data-testid={`projects-tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                activeTab === tab.key ? 'bg-iflow-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.label}
              <span className={`text-xs ${activeTab === tab.key ? 'text-white/80' : 'text-gray-400'}`}>{tabCounts[tab.key]}</span>
            </button>
          ))}
        </div>
        <input
          type="text"
          data-testid="dashboard-search"
          placeholder="Filter projects…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full sm:w-64 px-3 py-2 text-sm bg-white text-gray-900 placeholder-gray-500 border border-sky-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-iflow-400"
        />
      </div>

      {filteredProjects.length === 0 ? (
        <p className="text-gray-500 text-sm mb-4">
          {searchQuery ? <>No projects match &quot;{searchQuery}&quot;</> : 'No projects in this view.'}
        </p>
      ) : (
        <div className="light-card overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3">Project</th>
                <th className="px-4 py-3">Stage</th>
                <th className="px-4 py-3">Documents</th>
                <th className="px-4 py-3">Last updated</th>
                <th className="px-4 py-3 sr-only">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.map((project) => {
                const pill = getProjectStagePill(project, agreements)
                const isSelected = selectedForCompare.includes(project.project_id)
                const atLimit = selectedForCompare.length >= 3 && !isSelected
                return (
                  <tr
                    key={project.project_id}
                    data-testid={`project-card-${project.project_id}`}
                    onClick={() => {
                      if (compareMode) {
                        toggleCompareSelection(project)
                      } else {
                        // BUG-2: projects shared WITH the user open in the collaborator
                        // view (the owner ProjectView is gated to owners).
                        navigate(project.shared_with_me ? `/shared/project/${project.project_id}` : `/project/${project.project_id}`)
                      }
                    }}
                    className={`border-b border-gray-100 last:border-0 cursor-pointer transition-colors ${
                      compareMode ? (isSelected ? 'bg-iflow-500/10' : atLimit ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50') : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3 min-w-0">
                        {compareMode && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            disabled={atLimit}
                            className="h-4 w-4 rounded border-gray-300 text-iflow-500 accent-iflow-500 flex-shrink-0"
                            data-testid={`compare-checkbox-${project.project_id}`}
                            aria-label={`Select ${project.name} for comparison`}
                          />
                        )}
                        <span className="h-8 w-8 shrink-0 rounded-full bg-iflow-100 text-iflow-700 flex items-center justify-center text-xs font-semibold">
                          {projectInitials(project.name)}
                        </span>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900 truncate">{project.name}</span>
                            {project.shared_with_me && (
                              <span
                                className="flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-medium bg-iflow-500/10 text-iflow-700"
                                title="Shared with you by another inventor"
                                data-testid={`shared-badge-${project.project_id}`}
                              >
                                Shared
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 truncate">
                            {project.vertical ? `${project.vertical} · ` : ''}
                            you
                            {project.collaborator_count > 0
                              ? ` + ${project.collaborator_count} collaborator${project.collaborator_count === 1 ? '' : 's'}`
                              : ''}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusChip tone={pill.tone}>{pill.label}</StatusChip>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{project.document_count}</td>
                    <td className="px-4 py-3 text-gray-500">{formatRelativeTime(project.updated_at) || '—'}</td>
                    <td className="px-4 py-3 text-right relative">
                      {!project.shared_with_me && (
                        <>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              setOpenMenuId((id) => (id === project.project_id ? null : project.project_id))
                            }}
                            aria-label={`Actions for ${project.name}`}
                            data-testid={`project-actions-${project.project_id}`}
                            className="p-1.5 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100"
                          >
                            <MoreVertical size={16} />
                          </button>
                          {openMenuId === project.project_id && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setOpenMenuId(null) }} />
                              <div
                                role="menu"
                                aria-label={`Actions for ${project.name}`}
                                onClick={(e) => e.stopPropagation()}
                                className="absolute right-4 top-10 z-20 w-52 rounded-lg border border-slate-200 bg-white py-1 shadow-xl text-left"
                              >
                                <button
                                  role="menuitem"
                                  onClick={() => {
                                    setOpenMenuId(null)
                                    navigate(project.shared_with_me ? `/shared/project/${project.project_id}` : `/project/${project.project_id}`)
                                  }}
                                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                  <OpenWorkspaceIcon size={14} /> Open workspace
                                </button>
                                <button
                                  role="menuitem"
                                  onClick={() => {
                                    setOpenMenuId(null)
                                    navigate(uploadLinkFor(project.project_id))
                                  }}
                                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                  <Upload size={14} /> Add documents
                                </button>
                                <button
                                  role="menuitem"
                                  onClick={() => {
                                    setOpenMenuId(null)
                                    toggleDiscoverableMutation.mutate({ id: project.project_id, discoverable: !project.discoverable })
                                  }}
                                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                  {project.discoverable ? <EyeOff size={14} /> : <Eye size={14} />}
                                  {project.discoverable ? 'Make hidden' : 'Make discoverable'}
                                </button>
                                <button
                                  role="menuitem"
                                  onClick={() => {
                                    setOpenMenuId(null)
                                    setRenameValue(project.name)
                                    setRenameError('')
                                    setRenameTarget(project)
                                  }}
                                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                  <Pencil size={14} /> Rename
                                </button>
                                <div className="my-1 border-t border-slate-100" />
                                <button
                                  role="menuitem"
                                  onClick={() => {
                                    setOpenMenuId(null)
                                    setDeleteTarget(project)
                                  }}
                                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50"
                                >
                                  <Trash2 size={14} /> Archive…
                                </button>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* CP2: side-by-side comparison panel */}
      {compareMode && selectedForCompare.length > 0 && (
        <CompareProjectsPanel
          projects={projects.filter((p) => selectedForCompare.includes(p.project_id))}
          onClose={() => {
            setCompareMode(false)
            setSelectedForCompare([])
          }}
        />
      )}

      {renderCreateModal()}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Archive project"
        message={
          <>
            Archive <strong>{deleteTarget?.name}</strong>? It will be hidden from your project list. Its current documents, analysis, connections and permissions are retained. An authorised administrator can review reactivation in the Management Portal recovery workflow. Archiving does not erase the project data.
          </>
        }
        confirmLabel="Archive"
        tone="danger"
        onConfirm={async () => {
          if (!deleteTarget) return
          try {
            await deleteMutation.mutateAsync(deleteTarget.project_id)
          } catch (err) {
            const e = err as Error & { response?: { data?: { error?: string } } }
            throw new Error(e.response?.data?.error || e.message || 'Failed to archive project')
          }
        }}
        onClose={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={!!renameTarget}
        title="Rename project"
        message={
          <div className="mt-2">
            <label className="mb-1 block text-sm font-medium text-slate-700">Project name</label>
            <input
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              className="light-input w-full px-3 py-2 text-sm outline-none"
            />
            {renameError && <p className="mt-2 text-sm text-rose-600">{renameError}</p>}
          </div>
        }
        confirmLabel="Save"
        onConfirm={async () => {
          if (!renameTarget || !renameValue.trim()) return
          try {
            await renameMutation.mutateAsync({ id: renameTarget.project_id, name: renameValue.trim() })
          } catch (err) {
            const e = err as Error & { response?: { data?: { error?: string } } }
            throw new Error(e.response?.data?.error || e.message || 'Failed to rename project')
          }
        }}
        onClose={() => {
          setRenameTarget(null)
          setRenameError('')
        }}
      />

      <GrantAggregatorAccessDialog
        projectId={grantPromptProjectId ?? ''}
        isOpen={!!grantPromptProjectId}
        hideIfEmpty
        intro="Your project was created. Grant a managing aggregator access so it appears in their portfolio — or skip and do this later from the project's Aggregators tab."
        onClose={() => setGrantPromptProjectId(null)}
        onSuccess={() => setGrantPromptProjectId(null)}
      />
    </div>
  )
}
